import React, { useRef, useEffect, useCallback, useState, useMemo, lazy, Suspense } from 'react';
import { EditorPanel, PreviewPanel, ExportModal, VideoExportModal } from '../components/builder';
import { SideNav } from '../components/builder/SideNav';
import { LoginPanel, SignupPanel, AccountPanel } from '../components/auth';
import DonationContent from '../components/donation/DonationContent';
import { useCarousels, useSlides, useExport, useAssetHydration } from '../hooks';
import { useAuth } from '../context/AuthContext';
import { FOREST_BG_URLS } from '../constants/backgrounds';
import { DEFAULT_TEMPLATE } from '../templates';
import { scrollEditorToSection } from '../utils/scrollEditor';
import { resolveAssets } from '../utils/storageApi';
import { getAuthHeaders } from '../utils/authHeaders';
import {
  createShowcaseCarousel,
  publishShowcase,
  unpublishShowcase,
  deleteShowcase,
} from '../utils/showcaseApi';

const USE_CLERK = process.env.REACT_APP_USE_CLERK_AUTH === 'true';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Diagnostic log — no secrets, just boolean state
console.info('[auth] Clerk auth enabled:', USE_CLERK);

// Step 4: Lazy-load Clerk email-code panel (only when Clerk is enabled)
const ClerkEmailCodePanel = USE_CLERK
  ? lazy(() => import('../components/auth/ClerkEmailCodePanel'))
  : null;

// Fields that support inline editing in the preview (Phase 3).
const INLINE_EDITABLE_FIELDS = ['title', 'kicker', 'subtitle', 'body', 'ctaButton', 'cta'];

/**
 * WebGL noise texture for device body.
 * Renders a static noise pattern with overlay blend mode.
 */
function DeviceNoiseOverlay() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl');
    if (!gl) return;

    canvas.width = canvas.clientWidth * 2;
    canvas.height = canvas.clientHeight * 2;
    gl.viewport(0, 0, canvas.width, canvas.height);

    const vs = `
      attribute vec2 position;
      varying vec2 vUv;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
        vUv = position * 0.5 + 0.5;
      }
    `;
    const fs = `
      precision highp float;
      varying vec2 vUv;
      float random(vec2 st) { return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123); }
      void main() {
        float n = random(vUv * 10.0);
        gl_FragColor = vec4(vec3(0.0), n * 0.5);
      }
    `;

    const compile = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return s;
    };
    const prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, vs));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
    const pos = gl.getAttribLocation(prog, 'position');
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="device-noise-overlay"
      style={{ width: '100%', height: '100%' }}
    />
  );
}

/**
 * Main Visuals page component
 * Magic Forest Visuals - visuals.magicforest.land
 */
export default function PostBuilderPage() {
  // Ref to preview panel for export node
  const previewRef = useRef(null);

  // Auth state
  const { user, isAuthenticated, isAnonymous, isSeed, isLoading: isAuthLoading, onAuthChange, refreshAuth } = useAuth();

  // Panel view state: 'editor' | 'login' | 'signup' | 'account' | 'clerk-login'
  const [panelView, setPanelView] = useState('editor');

  // Donation panel state — separate from panelView so LCD header stays
  const [showDonation, setShowDonation] = useState(false);

  // Auth panel state — separate from panelView so LCD header stays
  const [showAuth, setShowAuth] = useState(false);

  // Account panel state — separate from panelView so LCD header stays
  const [showAccount, setShowAccount] = useState(false);

  // Slide management hook
  const {
    slides,
    setSlides,
    activeIndex,
    setActiveIndex,
    activeSlide,
    handleSlideChange,
    handleAddSlide,
    handleRemoveSlide,
    handleDuplicateSlide,
    handleReorderSlides,
    handlePrev,
    handleNext,
    dirtyRef,
    changeHintRef,
    handleBulkSlideChange,
  } = useSlides();

  // Carousel management hook (waits for auth to avoid race condition on first visit)
  const {
    carousels,
    activeCarouselId,
    isLoadingCarousels,
    isCreatingCarousel,
    conflictToast,
    saveCount,
    saveError,
    slidesRef,
    handleCarouselSelect,
    handleCarouselCreate,
    handleCarouselRename,
    handleCarouselDelete,
    autoSave,
    ensureCarouselPersisted,
    isLocalDemo,
    dismissConflictToast,
    dismissSaveError,
    refreshCarousels,
    handleTemplateChange,
    handleLogoChange,
  } = useCarousels(setSlides, setActiveIndex, dirtyRef, changeHintRef, !isAuthLoading);

  // ── Welcome Showcase: split carousels into regular vs showcase ──
  const regularCarousels = useMemo(
    () => carousels.filter(c => !c._showcase),
    [carousels],
  );
  const showcaseCarousels = useMemo(
    () => carousels.filter(c => !!c._showcase),
    [carousels],
  );
  const [isCreatingShowcase, setIsCreatingShowcase] = useState(false);

  const handleShowcaseCreate = useCallback(async () => {
    setIsCreatingShowcase(true);
    try {
      const sc = await createShowcaseCarousel('Showcase');
      await refreshCarousels();
      handleCarouselSelect(sc.id);
    } catch (err) {
      console.error('[showcase] Create failed:', err.message);
    } finally {
      setIsCreatingShowcase(false);
    }
  }, [refreshCarousels, handleCarouselSelect]);

  const handleShowcaseDelete = useCallback(async (id) => {
    try {
      await deleteShowcase(id);
      await refreshCarousels();
    } catch (err) {
      console.error('[showcase] Delete failed:', err.message);
    }
  }, [refreshCarousels]);

  const handleShowcasePublish = useCallback(async (id) => {
    try {
      await publishShowcase(id);
      await refreshCarousels();
    } catch (err) {
      console.error('[showcase] Publish failed:', err.message);
    }
  }, [refreshCarousels]);

  const handleShowcaseUnpublish = useCallback(async (id) => {
    try {
      await unpublishShowcase(id);
      await refreshCarousels();
    } catch (err) {
      console.error('[showcase] Unpublish failed:', err.message);
    }
  }, [refreshCarousels]);

  // Phase 5b: Video URL hydration — resolves asset_ids to presigned URLs
  const { resolveErrors, isHydrating } = useAssetHydration(slides, setSlides, dirtyRef);

  // Keep slidesRef in sync for flushSave during carousel switch
  useEffect(() => {
    slidesRef.current = slides;
  }, [slides, slidesRef]);

  // Re-fetch carousels when auth changes (login/signup/logout).
  // IMPORTANT: Flush any pending local-demo save BEFORE refreshing.
  // Without this, refreshCarousels() cancels the debounced autoSave timer,
  // and an unsaved local demo (anonymous carousel) is lost — the merge
  // endpoint then finds nothing to transfer (Bug 2: Step 5 second-login).
  useEffect(() => {
    if (!onAuthChange) return;
    const unsubscribe = onAuthChange(async () => {
      if (dirtyRef.current && isLocalDemo?.current && !activeCarouselId) {
        try {
          await ensureCarouselPersisted(slidesRef.current);
        } catch (err) {
          console.warn('[PostBuilderPage] Pre-merge flush failed:', err.message);
        }
      }
      refreshCarousels();
    });
    return unsubscribe;
  }, [onAuthChange, refreshCarousels, dirtyRef, isLocalDemo, activeCarouselId, ensureCarouselPersisted, slidesRef]);

  // Export functionality hook
  const {
    isExporting,
    exportUrl,
    exportSafe,
    isExportingJpeg,
    jpegExportUrl,
    isExportingVideo,
    videoExportUrl,
    videoExportProgress,
    exportError,
    dismissExportError,
    handleExport,
    handleExportJpeg,
    handleExportVideo,
    clearExportUrl,
    clearJpegExportUrl,
    clearVideoExportUrl,
  } = useExport(previewRef, activeIndex, activeSlide, activeCarouselId, resolveErrors);

  // Derive active template — per-slide first, then carousel fallback, then global default
  const activeCarousel = carousels.find(c => c.id === activeCarouselId);
  const activeTemplate = activeSlide?.template || activeCarousel?.template || DEFAULT_TEMPLATE;
  const activeLogoEnabled = activeCarousel?.logo_enabled !== false; // default true
  const activeLogoAssetId = activeCarousel?.logo_asset_id || null;

  // ── Logo asset resolution: resolve logo_asset_id → presigned URL ──
  const [resolvedLogoUrl, setResolvedLogoUrl] = useState(null);
  const logoResolveRef = useRef(null); // track current resolve to avoid stale updates

  useEffect(() => {
    if (activeLogoAssetId) {
      // Resolve asset_id to presigned URL
      const currentAssetId = activeLogoAssetId;
      logoResolveRef.current = currentAssetId;
      resolveAssets([currentAssetId]).then(result => {
        // Only apply if still the same asset (avoid race conditions on carousel switch)
        if (logoResolveRef.current !== currentAssetId) return;
        const item = result?.items?.find(i => i.asset_id === currentAssetId);
        if (item?.download_url) {
          setResolvedLogoUrl(item.download_url);
        }
      }).catch(err => {
        console.warn('[Logo] Failed to resolve logo asset:', err);
      });

      // Schedule refresh before presigned URL expires (every 45 min)
      const refreshTimer = setInterval(() => {
        resolveAssets([currentAssetId]).then(result => {
          if (logoResolveRef.current !== currentAssetId) return;
          const item = result?.items?.find(i => i.asset_id === currentAssetId);
          if (item?.download_url) {
            setResolvedLogoUrl(item.download_url);
          }
        }).catch(() => {});
      }, 45 * 60 * 1000);

      return () => clearInterval(refreshTimer);
    } else {
      setResolvedLogoUrl(null);
      logoResolveRef.current = null;
    }
  }, [activeLogoAssetId]);

  // Active logo URL: resolved S3 URL (preferred) → legacy logo_url → null
  const activeLogoUrl = resolvedLogoUrl || activeCarousel?.logo_url || null;
  const hasCustomLogo = !!activeLogoAssetId || !!activeCarousel?.logo_url;

  // For the SLIDE: default logo always shows "MAGIC FOREST" / "association";
  // custom logo shows whatever the user typed (empty = no text on slide)
  const activeLogoTitle = hasCustomLogo
    ? (activeCarousel?.logo_title || '')
    : 'MAGIC FOREST';
  const activeLogoSubtitle = hasCustomLogo
    ? (activeCarousel?.logo_subtitle || '')
    : 'association';
  const activeLogoSize = activeCarousel?.logo_size || null;
  const activeLogoIsDark = activeCarousel?.logo_is_dark || false;
  const activeLogoInvert = activeCarousel?.logo_invert ?? false;

  // When logo changes come back from LogoEditor with a transient download_url,
  // use it immediately (no resolve needed until next page load)
  const handleLogoChangeWithUrl = useCallback((updates) => {
    // If update includes a transient download_url from S3 upload, set it as resolved immediately
    if (updates.logo_url && updates.logo_asset_id) {
      setResolvedLogoUrl(updates.logo_url);
      // Persist asset_id + clear legacy logo_url (prevents stale URL flash on reload)
      const { logo_url, ...rest } = updates;
      handleLogoChange({ ...rest, logo_url: '' });
    } else if (updates.logo_asset_id === '') {
      // Clearing the logo entirely
      setResolvedLogoUrl(null);
      handleLogoChange(updates);
    } else {
      handleLogoChange(updates);
    }
  }, [handleLogoChange]);

  // Auto-save slides when they change
  useEffect(() => {
    autoSave(slides, activeCarouselId);
  }, [slides, activeCarouselId, autoSave]);

  // ── Export wrappers: persist local demo before exporting ──
  const wrappedHandleExport = useCallback(async () => {
    if (isLocalDemo?.current) {
      await ensureCarouselPersisted(slides);
    }
    handleExport();
  }, [handleExport, ensureCarouselPersisted, isLocalDemo, slides]);

  const wrappedHandleExportJpeg = useCallback(async () => {
    if (isLocalDemo?.current) {
      await ensureCarouselPersisted(slides);
    }
    handleExportJpeg();
  }, [handleExportJpeg, ensureCarouselPersisted, isLocalDemo, slides]);

  const wrappedHandleExportVideo = useCallback(async () => {
    if (isLocalDemo?.current) {
      await ensureCarouselPersisted(slides);
    }
    handleExportVideo();
  }, [handleExportVideo, ensureCarouselPersisted, isLocalDemo, slides]);

  // ── Sign-up nudge toasts for anonymous users ──
  const [nudgeToast, setNudgeToast] = useState(null);
  const nudgeFiredRef = useRef({ first: false, second: false });
  const nudgeTimerRef = useRef(null);

  useEffect(() => {
    if (!isAnonymous || saveCount === 0) return;
    if (saveCount >= 1 && !nudgeFiredRef.current.first) {
      nudgeFiredRef.current.first = true;
      setNudgeToast('Your changes are temporary — sign up to keep them.');
    } else if (saveCount >= 6 && !nudgeFiredRef.current.second) {
      nudgeFiredRef.current.second = true;
      setNudgeToast("You've made a lot of changes — sign up to avoid losing them.");
    }
  }, [saveCount, isAnonymous]);

  useEffect(() => {
    if (!nudgeToast) return;
    if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    nudgeTimerRef.current = setTimeout(() => setNudgeToast(null), 8000);
    return () => { if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current); };
  }, [nudgeToast]);

  const handleNudgeSignup = useCallback(() => {
    setNudgeToast(null);
    // Step 4: When Clerk is enabled, nudge to clerk-login instead of signup
    setPanelView(USE_CLERK ? 'clerk-login' : 'signup');
  }, []);

  // Keyboard navigation: Arrow Left/Right to switch slides
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrev, handleNext]);

  // Ctrl+Shift+S — download all carousels as a backup JSON file
  const handleDownloadBackup = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${BACKEND_URL}/api/carousels/export-all`, { headers });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : 'magic-forest-backup.json';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Backup download failed:', err);
      alert('Failed to download backup. Check the console for details.');
    }
  }, []);

  useEffect(() => {
    const handleBackupShortcut = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        handleDownloadBackup();
      }
    };
    window.addEventListener('keydown', handleBackupShortcut);
    return () => window.removeEventListener('keydown', handleBackupShortcut);
  }, [handleDownloadBackup]);

  // Preload all slide backgrounds
  useEffect(() => {
    const bgCount = FOREST_BG_URLS.length;
    slides.forEach((slide, i) => {
      const url = slide.backgroundUrl || FOREST_BG_URLS[(slide.bgIndex ?? i) % bgCount];
      if (url) {
        const img = new Image();
        img.src = url;
      }
    });
  }, [slides]);

  // Auth panel callbacks
  // When Clerk is enabled, login click opens inline auth inside EditorPanel
  const handleLoginClick = useCallback(() => {
    setShowDonation(false);
    setShowAccount(false);
    setShowAuth(true);
    setPanelView('editor');
  }, []);
  const handleAccountClick = useCallback(() => {
    setShowDonation(false);
    setShowAuth(false);
    setShowAccount(true);
    setPanelView('editor');
  }, []);
  const handleDonateClick = useCallback(() => {
    setShowAuth(false);
    setShowAccount(false);
    setPanelView('editor');
    setShowDonation(prev => !prev);
  }, []);
  const handleDonateClose = useCallback(() => setShowDonation(false), []);
  const handleAuthClose = useCallback(() => setShowAuth(false), []);
  const handleAccountClose = useCallback(() => setShowAccount(false), []);
  const handleBackToEditor = useCallback(() => {
    setPanelView('editor');
    setShowAuth(false);
    setShowDonation(false);
    setShowAccount(false);
  }, []);
  const handleAuthSuccess = useCallback(() => {
    setPanelView('editor');
    setShowAuth(false);
    // When Clerk sign-in completes, refresh auth to get local user
    if (USE_CLERK && refreshAuth) {
      refreshAuth();
    }
  }, [refreshAuth]);

  // ── Phase 3: Inline editing state ──
  // Which field (if any) is being edited directly in the preview canvas.
  // null = not editing. 'title' = title contentEditable active, etc.
  const [editingField, setEditingField] = useState(null);

  // Clear editing when slide changes
  useEffect(() => {
    setEditingField(null);
  }, [activeIndex]);

  // ── Preview click-to-scroll + inline editing ──
  //    Clicking a content element in the preview:
  //    1. Starts inline editing (if supported) — focus stays in preview
  //    2. Scrolls editor panel to the matching section (convenience)
  //    3. For non-inline fields: focuses the editor input (Phase 2 fallback)
  const handleElementClick = useCallback((sectionId, fieldName) => {
    // Switch to editor panel if in auth view
    if (panelView !== 'editor') setPanelView('editor');

    const supportsInlineEdit = INLINE_EDITABLE_FIELDS.includes(fieldName);

    if (supportsInlineEdit) {
      // Phase 3: start inline editing — focus handled by InlineEditable
      setEditingField(fieldName);
    }

    requestAnimationFrame(() => {
      // Safe scroll — only scrolls editor container, never parent viewport
      scrollEditorToSection(sectionId);

      // Phase 2 fallback: focus editor input for non-inline fields
      if (!supportsInlineEdit && fieldName) {
        setTimeout(() => {
          const fieldEl = document.querySelector(`[data-editor-field="${fieldName}"]`);
          if (!fieldEl) return;
          const focusable = fieldEl.querySelector(
            '[contenteditable="true"], textarea, input:not([type="checkbox"]):not([type="radio"])'
          );
          if (focusable) {
            focusable.focus();
          }
          // LED flash disabled — code preserved in CSS (.editor-field-led)
        }, 450);
      }
    });
  }, [panelView]);

  // Sync inline edits from preview → slide state (two-way sync).
  // Uses setSlides functional update to always read latest state — avoids
  // stale-closure issues during rapid keystrokes in contentEditable.
  const handleEditChange = useCallback((fieldName, value) => {
    setSlides(prev => {
      const slide = prev[activeIndex];
      if (!slide || slide[fieldName] === value) return prev;

      dirtyRef.current = true;
      changeHintRef.current = `Slide ${activeIndex + 1} ${fieldName} was edited`;

      const next = [...prev];
      next[activeIndex] = { ...slide, [fieldName]: value };
      return next;
    });
  }, [activeIndex, setSlides]);

  // End inline editing session
  const handleEndEdit = useCallback(() => {
    setEditingField(null);
  }, []);

  // ── Background click — scroll editor to the active background module ──
  //    Determines which bg type is active and scrolls to the precise sub-section.
  const handleBackgroundClick = useCallback(() => {
    // End any inline editing
    setEditingField(null);

    if (panelView !== 'editor') setPanelView('editor');

    let sectionId;
    if (activeSlide?.primitives3d?.enabled)   sectionId = 'bg-primitives';
    else if (activeSlide?.generative?.enabled) sectionId = 'genart';
    else if (activeSlide?.gradient?.enabled)   sectionId = 'bg-gradient';
    else if (activeSlide?.bgSolid)             sectionId = 'bg-solid';
    else                                        sectionId = 'bg-image';

    requestAnimationFrame(() => {
      scrollEditorToSection(sectionId);
    });
  }, [panelView, activeSlide]);

  // ── Layout Nudge — commit drag offset to slide data (template-scoped) ──
  const handleNudgeEnd = useCallback((groupId, offset) => {
    setSlides(prev => {
      const slide = prev[activeIndex];
      if (!slide) return prev;

      // Get current template-scoped nudge data
      const allNudge = slide.layoutNudge || {};
      const templateNudge = allNudge[activeTemplate] || {};

      const groupNudge = { dx: Math.round(offset.dx), dy: Math.round(offset.dy) };
      // If offset is zero (snapped back to default), remove the group key
      const isZero = groupNudge.dx === 0 && groupNudge.dy === 0;
      const newTemplateNudge = { ...templateNudge };

      if (isZero) {
        delete newTemplateNudge[groupId];
      } else {
        newTemplateNudge[groupId] = groupNudge;
      }

      // Build new full nudge object
      const newNudge = { ...allNudge };
      if (Object.keys(newTemplateNudge).length === 0) {
        delete newNudge[activeTemplate];
      } else {
        newNudge[activeTemplate] = newTemplateNudge;
      }

      const newLayoutNudge = Object.keys(newNudge).length > 0 ? newNudge : undefined;

      // Check if nudge actually changed
      const oldVal = JSON.stringify(slide.layoutNudge);
      const newVal = JSON.stringify(newLayoutNudge);
      if (oldVal === newVal) return prev;

      dirtyRef.current = true;
      changeHintRef.current = `Slide ${activeIndex + 1} layout was adjusted`;

      const next = [...prev];
      next[activeIndex] = {
        ...slide,
        layoutNudge: newLayoutNudge,
      };
      return next;
    });
  }, [activeIndex, activeTemplate, setSlides]);

  // ── Content Element Reorder (Phase 3 — Session 48) ──
  const handleReorder = useCallback((newContentOrder) => {
    setSlides(prev => {
      const slide = prev[activeIndex];
      if (!slide) return prev;

      // Check if order actually changed
      const oldVal = JSON.stringify(slide.contentOrder);
      const newVal = JSON.stringify(newContentOrder || undefined);
      if (oldVal === newVal) return prev;

      dirtyRef.current = true;
      changeHintRef.current = `Slide ${activeIndex + 1} content order was changed`;

      const next = [...prev];
      next[activeIndex] = {
        ...slide,
        contentOrder: newContentOrder || undefined,
      };
      return next;
    });
  }, [activeIndex, setSlides]);

  // Render left panel based on panelView state
  const renderLeftPanel = () => {
    switch (panelView) {
      case 'login':
        return (
          <LoginPanel
            onBack={handleBackToEditor}
            onSwitchToSignup={() => { setShowAuth(true); setPanelView('editor'); }}
            onSwitchToClerk={USE_CLERK ? () => { setShowAuth(true); setPanelView('editor'); } : undefined}
            onSuccess={handleAuthSuccess}
          />
        );
      default:
        return (
          <EditorPanel
            slides={slides}
            activeIndex={activeIndex}
            onSlideChange={handleSlideChange}
            onIndexChange={setActiveIndex}
            onAddSlide={handleAddSlide}
            onRemoveSlide={handleRemoveSlide}
            onDuplicateSlide={handleDuplicateSlide}
            onReorderSlides={handleReorderSlides}
            carousels={regularCarousels}
            activeCarouselId={activeCarouselId}
            onCarouselSelect={handleCarouselSelect}
            onCarouselCreate={handleCarouselCreate}
            onCarouselRename={handleCarouselRename}
            onCarouselDelete={handleCarouselDelete}
            isLoadingCarousels={isLoadingCarousels}
            isCreatingCarousel={isCreatingCarousel}
            // Welcome Showcase props (admin only)
            isSeed={isSeed}
            showcaseCarousels={showcaseCarousels}
            onShowcaseSelect={handleCarouselSelect}
            onShowcaseCreate={handleShowcaseCreate}
            onShowcaseRename={handleCarouselRename}
            onShowcaseDelete={handleShowcaseDelete}
            onShowcasePublish={handleShowcasePublish}
            onShowcaseUnpublish={handleShowcaseUnpublish}
            isCreatingShowcase={isCreatingShowcase}
            user={user}
            isAuthenticated={isAuthenticated}
            isAnonymous={isAnonymous}
            onLoginClick={handleLoginClick}
            onAccountClick={handleAccountClick}
            onDonateClick={handleDonateClick}
            showDonation={showDonation}
            onDonateClose={handleDonateClose}
            showAuth={showAuth}
            onAuthClose={handleAuthClose}
            onAuthSuccess={handleAuthSuccess}
            onSwitchToLegacyLogin={() => { setShowAuth(false); setPanelView('login'); }}
            showAccount={showAccount}
            onAccountClose={handleAccountClose}
            onBackToEditor={handleBackToEditor}
            userEmail={user?.email || ''}
            template={activeTemplate}
            onTemplateChange={handleTemplateChange}
            onBulkSlideChange={handleBulkSlideChange}
            logoEnabled={activeLogoEnabled}
            logoUrl={activeLogoUrl}
            logoAssetId={activeLogoAssetId}
            logoTitle={activeLogoTitle}
            logoSubtitle={activeLogoSubtitle}
            logoSize={activeLogoSize}
            logoIsDark={activeLogoIsDark}
            logoInvert={activeLogoInvert}
            onLogoChange={handleLogoChangeWithUrl}
            resolveErrors={resolveErrors}
          />
        );
    }
  };

  // ── Device scaling: fixed-size device scales uniformly to fit viewport ──
  const DEVICE_W = 1260;
  const DEVICE_H = 720;
  const VIEWPORT_PADDING = 24; // px breathing room around device
  const viewportRef = useRef(null);
  const [deviceScale, setDeviceScale] = useState(1);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const compute = () => {
      const vw = el.clientWidth;
      const vh = el.clientHeight;
      const sx = (vw - VIEWPORT_PADDING * 2) / DEVICE_W;
      const sy = (vh - VIEWPORT_PADDING * 2) / DEVICE_H;
      setDeviceScale(Math.min(sx, sy)); // scales up or down to fill viewport
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={viewportRef}
      className="h-screen w-screen flex items-center justify-center overflow-hidden"
      style={{ background: 'radial-gradient(circle at 50% 30%, #4a4b4e 0%, #151516 100%)' }}
    >
      {/* Device — fixed pixel dimensions, scaled uniformly */}
      <div
        className="device-frame"
        style={{ transform: `scale(${deviceScale})` }}
      >
        {/* Noise texture overlay */}
        <DeviceNoiseOverlay />

        {/* Engraved text labels */}
        <a href="https://magicforest.land" target="_blank" rel="noopener noreferrer" className="device-engraving device-engraving-link" style={{ top: 14, left: 24 }}>Magic Forest Association</a>
        <div className="device-engraving" style={{ top: 14, right: 24 }}>MOD-01 / VISUALS</div>
        <div className="device-engraving" style={{ bottom: 14, left: '50%', transform: 'translateX(-50%)' }}>PRECISION DESIGN LINK</div>

        {/* ─── Left: Section navigation ─── */}
        {panelView === 'editor' && (
          <SideNav />
        )}

        {/* ─── Middle: Editor / Auth panels ─── */}
        <div
          className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden"
          style={{ zIndex: 5, paddingLeft: panelView !== 'editor' ? 40 : 0 }}
        >
          {renderLeftPanel()}
        </div>

        {/* ─── Right: Preview in screen bezel ─── */}
        <div className="w-[460px] flex-shrink-0 flex flex-col ml-6" style={{ zIndex: 5 }}>
          <PreviewPanel
              ref={previewRef}
              activeSlide={activeSlide}
              activeIndex={activeIndex}
              totalSlides={slides.length}
              onPrev={handlePrev}
              onNext={handleNext}
              onExport={wrappedHandleExport}
              onExportJpeg={wrappedHandleExportJpeg}
              onExportVideo={wrappedHandleExportVideo}
              isExporting={isExporting}
              isExportingJpeg={isExportingJpeg}
              isExportingVideo={isExportingVideo}
              videoExportProgress={videoExportProgress}
              exportSafe={exportSafe}
              exportError={exportError}
              onDismissExportError={dismissExportError}
              template={activeTemplate}
              logoEnabled={activeLogoEnabled}
              logoUrl={activeLogoUrl}
              logoTitle={activeLogoTitle}
              logoSubtitle={activeLogoSubtitle}
              logoSize={activeLogoSize}
              logoIsDark={activeLogoInvert}
              onElementClick={handleElementClick}
              editingField={editingField}
              onEditChange={handleEditChange}
              onEndEdit={handleEndEdit}
              onBackgroundClick={handleBackgroundClick}
              onNudgeEnd={handleNudgeEnd}
              onReorder={handleReorder}
            />
          </div>
        </div>

      {/* Export Modal (PNG) */}
      <ExportModal
        exportUrl={exportUrl}
        slideIndex={activeIndex}
        format="png"
        bgRounded={activeSlide?.bgRounded === true}
        onClose={clearExportUrl}
      />
      
      {/* Export Modal (JPEG) */}
      <ExportModal
        exportUrl={jpegExportUrl}
        slideIndex={activeIndex}
        format="jpeg"
        bgRounded={activeSlide?.bgRounded === true}
        onClose={clearJpegExportUrl}
      />
      
      {/* Video Export Modal */}
      <VideoExportModal
        exportUrl={videoExportUrl}
        slideIndex={activeIndex}
        duration={activeSlide?.videoWidget?.duration}
        bgRounded={activeSlide?.bgRounded === true}
        onClose={clearVideoExportUrl}
      />

      {/* Sync Toast */}
      {conflictToast && !isAnonymous && (
        <div className="fixed bottom-6 left-0 right-0 z-50 flex justify-center animate-slide-up">
          <div className="flex items-center gap-3 bg-emerald-900/90 border border-emerald-500/30 text-emerald-100 px-5 py-3 rounded-xl shadow-2xl backdrop-blur-sm">
            <svg className="w-5 h-5 flex-shrink-0 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm font-medium">{conflictToast}</span>
            <button
              onClick={dismissConflictToast}
              className="ml-2 text-emerald-400 hover:text-emerald-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Save Error Toast */}
      {saveError && (
        <div className="fixed bottom-6 left-0 right-0 z-50 flex justify-center animate-slide-up">
          <div className="flex items-center gap-3 bg-red-900/90 border border-red-500/30 text-red-100 px-5 py-3 rounded-xl shadow-2xl backdrop-blur-sm">
            <svg className="w-5 h-5 flex-shrink-0 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-sm font-medium">{saveError}</span>
            <button
              onClick={dismissSaveError}
              className="ml-2 text-red-400 hover:text-red-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Sign-up nudge toast */}
      {nudgeToast && (
        <div className="fixed bottom-6 left-0 right-0 z-50 flex justify-center animate-slide-up">
          <div className="flex items-center gap-3 bg-amber-900/90 border border-amber-500/30 text-amber-100 px-5 py-3 rounded-xl shadow-2xl backdrop-blur-sm">
            <svg className="w-5 h-5 flex-shrink-0 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
            </svg>
            <span className="text-sm font-medium">{nudgeToast}</span>
            <button
              onClick={handleNudgeSignup}
              className="ml-1 text-sm font-semibold text-amber-300 hover:text-amber-100 underline underline-offset-2 transition-colors whitespace-nowrap"
            >
              Sign up
            </button>
            <button
              onClick={() => setNudgeToast(null)}
              className="ml-1 text-amber-400 hover:text-amber-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
